import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import AnalyticsService from "../../../../services/AnalyticsService";
import { format } from "date-fns";
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import CategoryService from "../../../../services/CategoryService";
import UserService from "../../../../services/UserService";
import CustomerService from "../../../../services/CustomerService";
import TaskService from "../../../../services/TaskService";
import Cards from "../components/Cards";
import Filters from "../components/Filters";
import { IoSwitch } from "react-icons/io5";
import { colors } from '../../../config/colors';
import Charts from "../components/Charts";
import { getCharts } from "./constants/charts";
import { getJobTitleLabel, getJobTitleLabelPlural } from "../../../utils/jobTitleLabel";

const formatTimeToHoursAndMinutes = (minutes) => {
  if (!minutes) return '0m';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (hours === 0) return `${remainingMinutes}m`;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

function getComparisonCards() {
  const jobLabel = getJobTitleLabel();
  const jobPluralLabel = getJobTitleLabelPlural();
  return [
    { name: "totalJobs", displayName: `Total ${jobPluralLabel}`, tooltip: `The total number of ${jobPluralLabel.toLowerCase()} created by your organisation.` },
    { name: "totalRevenue", displayName: "Total Revenue", prefix: "$", tooltip: `Total revenue generated from all ${jobPluralLabel.toLowerCase()} in the selected period.` },
    { name: "avgVisitLength", displayName: "Avg Visit Length", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Average duration of each ${jobLabel.toLowerCase()} visit, calculated in hours and minutes.` },
    { name: "avgRevenue", displayName: "Avg Revenue", prefix: "$", tooltip: `Average revenue per ${jobLabel.toLowerCase()}, calculated by dividing total revenue by the number of ${jobPluralLabel.toLowerCase()}.`, tooltipPosition: "left" },
    { name: "avgTimeToReach", displayName: "Avg Time to Reach", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Average time taken by employees to reach the ${jobLabel.toLowerCase()} location.` },
    { name: "avgWaitTime", displayName: "Avg Wait Time", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Average waiting time before a ${jobLabel.toLowerCase()} starts after arrival at the location.` },
    { name: "avgTasksPerVisit", displayName: "Avg Tasks Per Visit", tooltip: `Average number of tasks completed during each ${jobLabel.toLowerCase()} visit.` },
    { name: "employeeCreatedJobs", displayName: `Employee Created ${jobPluralLabel}`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} created directly by employees.`, tooltipPosition: "left" },
    { name: "avgJobTime", displayName: `Avg ${jobLabel} Time`, valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Average time taken to complete a ${jobLabel.toLowerCase()} from start to finish.` },
    { name: "jobsBelowThreshold", displayName: `${jobPluralLabel} Completed Early`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} completed before the expected time threshold.` },
    { name: "jobsAboveThreshold", displayName: `${jobPluralLabel} Completed Late`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} completed after the expected time threshold.`, tooltipPosition: "left" },
    { name: "totalEmployeeAddedTasks", displayName: "Total Employee Added Tasks", tooltip: `Total number of tasks added by employees during ${jobLabel.toLowerCase()} execution.` },
    { name: "jobsWithEmployeeAddedTasks", displayName: `${jobPluralLabel} with Employee Added Tasks`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} where employees added additional tasks.`, tooltipPosition: "left" },
    { name: "avgEmployeeAddedTasksPerJob", displayName: `Avg Employee Added Tasks Per ${jobLabel}`, tooltip: `Average number of tasks added by employees per ${jobLabel.toLowerCase()}.` },
    { name: "avgEmployeeAddedTaskTime", displayName: "Avg Employee Added Task Time", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: "Average time spent on employee-added tasks.", tooltipPosition: "left" },
    { name: "totalPauseEvents", displayName: "Total Pause Events", tooltip: `Total number of times ${jobPluralLabel.toLowerCase()} were paused during execution.` },
    { name: "totalPauseTime", displayName: "Total Pause Time", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Total time spent in paused state across all ${jobPluralLabel.toLowerCase()}.`, tooltipPosition: "left" },
    { name: "jobsWithPauses", displayName: `${jobPluralLabel} with Pauses`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} that had pause events during execution.` },
    { name: "avgPauseEventsPerJob", displayName: `Avg Pause Events Per ${jobLabel}`, tooltip: `Average number of pause events per ${jobLabel.toLowerCase()}.`, tooltipPosition: "left" },
    { name: "avgPauseTime", displayName: "Avg Pause Time", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Average pause time per ${jobLabel.toLowerCase()}.` }
  ];
}

// Sample chart data
const sampleChartData = {
  jobsPerDay: [
    { date: '2024-03-01', filter1Jobs: 12, filter2Jobs: 8 },
    { date: '2024-03-02', filter1Jobs: 15, filter2Jobs: 10 },
    { date: '2024-03-03', filter1Jobs: 18, filter2Jobs: 14 },
    { date: '2024-03-04', filter1Jobs: 14, filter2Jobs: 12 },
    { date: '2024-03-05', filter1Jobs: 20, filter2Jobs: 15 },
    { date: '2024-03-06', filter1Jobs: 16, filter2Jobs: 13 },
    { date: '2024-03-07', filter1Jobs: 22, filter2Jobs: 18 }
  ],
  revenuePerDay: [
    { date: '2024-03-01', filter1Revenue: 6000, filter2Revenue: 4000 },
    { date: '2024-03-02', filter1Revenue: 7500, filter2Revenue: 5000 },
    { date: '2024-03-03', filter1Revenue: 9000, filter2Revenue: 7000 },
    { date: '2024-03-04', filter1Revenue: 7000, filter2Revenue: 6000 },
    { date: '2024-03-05', filter1Revenue: 10000, filter2Revenue: 7500 },
    { date: '2024-03-06', filter1Revenue: 8000, filter2Revenue: 6500 },
    { date: '2024-03-07', filter1Revenue: 11000, filter2Revenue: 9000 }
  ],
  timePerJob: [
    { date: '2024-03-01', filter1TimeSpent: 110, filter2TimeSpent: 100 },
    { date: '2024-03-02', filter1TimeSpent: 120, filter2TimeSpent: 105 },
    { date: '2024-03-03', filter1TimeSpent: 115, filter2TimeSpent: 110 },
    { date: '2024-03-04', filter1TimeSpent: 125, filter2TimeSpent: 115 },
    { date: '2024-03-05', filter1TimeSpent: 130, filter2TimeSpent: 120 },
    { date: '2024-03-06', filter1TimeSpent: 120, filter2TimeSpent: 110 },
    { date: '2024-03-07', filter1TimeSpent: 125, filter2TimeSpent: 115 }
  ],
  tasksPerVisit: [
    { date: '2024-03-01', filter1Tasks: 3.5, filter2Tasks: 3.0 },
    { date: '2024-03-02', filter1Tasks: 4.0, filter2Tasks: 3.2 },
    { date: '2024-03-03', filter1Tasks: 3.8, filter2Tasks: 3.5 },
    { date: '2024-03-04', filter1Tasks: 4.2, filter2Tasks: 3.8 },
    { date: '2024-03-05', filter1Tasks: 4.5, filter2Tasks: 4.0 },
    { date: '2024-03-06', filter1Tasks: 4.0, filter2Tasks: 3.5 },
    { date: '2024-03-07', filter1Tasks: 4.2, filter2Tasks: 3.8 }
  ]
};

const ComparisonDashboard = () => {
  const [cards, setCards] = useState(() => getComparisonCards());
  const [chartConfigs, setChartConfigs] = useState(() => getCharts());
  const [showDifferenceChange, setShowDifferenceChange] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    const handleLabelUpdate = () => {
      setCards(getComparisonCards());
      setChartConfigs(getCharts());
    };
    window.addEventListener("jobTitleLabelUpdated", handleLabelUpdate);
    return () => window.removeEventListener("jobTitleLabelUpdated", handleLabelUpdate);
  }, []);
  const [chartData, setChartData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  // Separate task states for each filter set
  const [tasks1, setTasks1] = useState([]);
  const [tasks2, setTasks2] = useState([]);
  
  // Filter 1 states
  const [selectedCategories1, setSelectedCategories1] = useState([]);
  const [selectedEmployees1, setSelectedEmployees1] = useState([]);
  const [selectedCustomers1, setSelectedCustomers1] = useState([]);
  const [selectedTasks1, setSelectedTasks1] = useState([]);
  const [showCategoryDropdown1, setShowCategoryDropdown1] = useState(false);
  const [showEmployeeDropdown1, setShowEmployeeDropdown1] = useState(false);
  const [showCustomerDropdown1, setShowCustomerDropdown1] = useState(false);
  const [showTaskDropdown1, setShowTaskDropdown1] = useState(false);
  const [categorySearchTerm1, setCategorySearchTerm1] = useState("");
  const [employeeSearchTerm1, setEmployeeSearchTerm1] = useState("");
  const [customerSearchTerm1, setCustomerSearchTerm1] = useState("");
  const [taskSearchTerm1, setTaskSearchTerm1] = useState("");
  const [dateRange1, setDateRange1] = useState([{
    startDate: null,
    endDate: null,
    key: 'selection1'
  }]);
  const [showDatePicker1, setShowDatePicker1] = useState(false);

  // Filter 2 states
  const [selectedCategories2, setSelectedCategories2] = useState([]);
  const [selectedEmployees2, setSelectedEmployees2] = useState([]);
  const [selectedCustomers2, setSelectedCustomers2] = useState([]);
  const [selectedTasks2, setSelectedTasks2] = useState([]);
  const [showCategoryDropdown2, setShowCategoryDropdown2] = useState(false);
  const [showEmployeeDropdown2, setShowEmployeeDropdown2] = useState(false);
  const [showCustomerDropdown2, setShowCustomerDropdown2] = useState(false);
  const [showTaskDropdown2, setShowTaskDropdown2] = useState(false);
  const [categorySearchTerm2, setCategorySearchTerm2] = useState("");
  const [employeeSearchTerm2, setEmployeeSearchTerm2] = useState("");
  const [customerSearchTerm2, setCustomerSearchTerm2] = useState("");
  const [taskSearchTerm2, setTaskSearchTerm2] = useState("");
  const [dateRange2, setDateRange2] = useState([{
    startDate: null,
    endDate: null,
    key: 'selection2'
  }]);
  const [showDatePicker2, setShowDatePicker2] = useState(false);

  // Refs for dropdowns
  const categoryDropdownRef1 = useRef(null);
  const employeeDropdownRef1 = useRef(null);
  const customerDropdownRef1 = useRef(null);
  const taskDropdownRef1 = useRef(null);
  const datePickerRef1 = useRef(null);
  const categoryDropdownRef2 = useRef(null);
  const employeeDropdownRef2 = useRef(null);
  const customerDropdownRef2 = useRef(null);
  const taskDropdownRef2 = useRef(null);
  const datePickerRef2 = useRef(null);

  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchChartsData();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const filters1 = {
        categoryIds: selectedCategories1,
        customerIds: selectedCustomers1,
        userIds: selectedEmployees1,
        taskIds: selectedTasks1,
        startDate: dateRange1[0]?.startDate ? format(dateRange1[0].startDate, 'yyyy-MM-dd') : null,
        endDate: dateRange1[0]?.endDate ? format(dateRange1[0].endDate, 'yyyy-MM-dd') : null,
      };

      const filters2 = {
        categoryIds: selectedCategories2,
        customerIds: selectedCustomers2,
        userIds: selectedEmployees2,
        taskIds: selectedTasks2,
        startDate: dateRange2[0]?.startDate ? format(dateRange2[0].startDate, 'yyyy-MM-dd') : null,
        endDate: dateRange2[0]?.endDate ? format(dateRange2[0].endDate, 'yyyy-MM-dd') : null,
      };

      const response = await AnalyticsService.getAdminComparisonAnalytics(filters1, filters2);
      if (response.success) {
        setAnalytics(response.data);
      } else {
        throw new Error("Failed to fetch analytics");
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to fetch analytics data");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchChartsData = async () => {
    try {
      setChartsLoading(true);
      const filters1 = {
        categoryIds: selectedCategories1,
        customerIds: selectedCustomers1,
        userIds: selectedEmployees1,
        taskIds: selectedTasks1,
        startDate: dateRange1[0]?.startDate ? format(dateRange1[0].startDate, 'yyyy-MM-dd') : null,
        endDate: dateRange1[0]?.endDate ? format(dateRange1[0].endDate, 'yyyy-MM-dd') : null,
      };

      const filters2 = {
        categoryIds: selectedCategories2,
        customerIds: selectedCustomers2,
        userIds: selectedEmployees2,
        taskIds: selectedTasks2,
        startDate: dateRange2[0]?.startDate ? format(dateRange2[0].startDate, 'yyyy-MM-dd') : null,
        endDate: dateRange2[0]?.endDate ? format(dateRange2[0].endDate, 'yyyy-MM-dd') : null,
      };

      const response = await AnalyticsService.getAdminComparisonCharts(filters1, filters2);

      if (response.success) {
        setChartData(response.data);
      } else {
        throw new Error("Failed to fetch analytics");
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to fetch analytics data");
    } finally {
      setChartsLoading(false);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const [categoriesRes, employeesRes, customersRes] = await Promise.all([
        CategoryService.getAllCategories(false),
        UserService.getUsers({ role: 'EMPLOYEE' }),
        CustomerService.getCustomers(1, 1000),
      ]);

      if (categoriesRes.success) setCategories(categoriesRes.data);
      if (employeesRes.data.status === "success") setEmployees(employeesRes.data.data.users);
      if (customersRes.data) setCustomers(customersRes.data.data);
    } catch (error) {
      console.error("Error fetching filter options:", error);
      toast.error("Failed to load filter options");
    }
  };

  // Fetch tasks for selected categories for Filter Set 1
  const fetchTasksForCategories1 = async () => {
    if (selectedCategories1.length === 0) {
      setTasks1([]);
      setSelectedTasks1([]);
      return;
    }

    try {
      const response = await TaskService.getTasks(1, 1000, "", selectedCategories1.join(','));
      if (response.success) {
        setTasks1(response.data);
      } else {
        throw new Error("Failed to fetch tasks");
      }
    } catch (error) {
      console.error("Error fetching tasks for categories:", error);
      toast.error("Failed to load tasks for selected categories");
    }
  };

  // Fetch tasks for selected categories for Filter Set 2
  const fetchTasksForCategories2 = async () => {
    if (selectedCategories2.length === 0) {
      setTasks2([]);
      setSelectedTasks2([]);
      return;
    }

    try {
      const response = await TaskService.getTasks(1, 1000, "", selectedCategories2.join(','));
      if (response.success) {
        setTasks2(response.data);
      } else {
        throw new Error("Failed to fetch tasks");
      }
    } catch (error) {
      console.error("Error fetching tasks for categories:", error);
      toast.error("Failed to load tasks for selected categories");
    }
  };

  // Add useEffect to fetch tasks when categories change for Filter Set 1
  useEffect(() => {
    fetchTasksForCategories1();
  }, [selectedCategories1]);

  // Add useEffect to fetch tasks when categories change for Filter Set 2
  useEffect(() => {
    fetchTasksForCategories2();
  }, [selectedCategories2]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!categoryDropdownRef1.current?.contains(event.target)) setShowCategoryDropdown1(false);
      if (!employeeDropdownRef1.current?.contains(event.target)) setShowEmployeeDropdown1(false);
      if (!customerDropdownRef1.current?.contains(event.target)) setShowCustomerDropdown1(false);
      if (!taskDropdownRef1.current?.contains(event.target)) setShowTaskDropdown1(false);
      if (!datePickerRef1.current?.contains(event.target)) setShowDatePicker1(false);
      if (!categoryDropdownRef2.current?.contains(event.target)) setShowCategoryDropdown2(false);
      if (!employeeDropdownRef2.current?.contains(event.target)) setShowEmployeeDropdown2(false);
      if (!customerDropdownRef2.current?.contains(event.target)) setShowCustomerDropdown2(false);
      if (!taskDropdownRef2.current?.contains(event.target)) setShowTaskDropdown2(false);
      if (!datePickerRef2.current?.contains(event.target)) setShowDatePicker2(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add useEffect for initial load
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Filter handlers
  const filteredCategories1 = categories?.filter(cat => 
    cat.categoryType.toLowerCase().includes(categorySearchTerm1.toLowerCase())
  );
  const filteredEmployees1 = employees?.filter(emp => 
    emp.name.toLowerCase().includes(employeeSearchTerm1.toLowerCase())
  );
  const filteredCustomers1 = customers?.filter(cust => 
    cust.name.toLowerCase().includes(customerSearchTerm1.toLowerCase())
  );
  const filteredTasks1 = tasks1?.filter(task => 
    task.title.toLowerCase().includes(taskSearchTerm1.toLowerCase())
  );

  const filteredCategories2 = categories?.filter(cat => 
    cat.categoryType.toLowerCase().includes(categorySearchTerm2.toLowerCase())
  );
  const filteredEmployees2 = employees?.filter(emp => 
    emp.name.toLowerCase().includes(employeeSearchTerm2.toLowerCase())
  );
  const filteredCustomers2 = customers?.filter(cust => 
    cust.name.toLowerCase().includes(customerSearchTerm2.toLowerCase())
  );
  const filteredTasks2 = tasks2?.filter(task => 
    task.title.toLowerCase().includes(taskSearchTerm2.toLowerCase())
  );

  const handleExport = async () => {
    try {
      const filters1 = {
        categoryIds: selectedCategories1,
        customerIds: selectedCustomers1,
        userIds: selectedEmployees1,
        taskIds: selectedTasks1,
        startDate: dateRange1[0]?.startDate ? format(dateRange1[0].startDate, 'yyyy-MM-dd') : null,
        endDate: dateRange1[0]?.endDate ? format(dateRange1[0].endDate, 'yyyy-MM-dd') : null,
      };

      const filters2 = {
        categoryIds: selectedCategories2,
        customerIds: selectedCustomers2,
        userIds: selectedEmployees2,
        taskIds: selectedTasks2,
        startDate: dateRange2[0]?.startDate ? format(dateRange2[0].startDate, 'yyyy-MM-dd') : null,
        endDate: dateRange2[0]?.endDate ? format(dateRange2[0].endDate, 'yyyy-MM-dd') : null,
      };

      const response = await AnalyticsService.exportComparisonAnalytics(filters1, filters2);
      
      if (response.success) {
        // Create a download link
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `job-comparison-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Comparison data exported successfully');
      } else {
        throw new Error(response.message || 'Failed to export comparison data');
      }
    } catch (error) {
      console.error('Error exporting comparison data:', error);
      toast.error('Failed to export comparison data');
    }
  };

  return (
    <div className="h-[calc(100vh-80px)] overflow-auto">
      <div className="min-h-full p-6">
        <div className="max-w-[1920px] mx-auto">
          <div className="space-y-8">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="space-y-1">
                <h2 className="text-2xl font-semibold text-[#2B3674]">
                  {`${getJobTitleLabel()} Analytics Comparison`}
                </h2>
                <p className="text-sm text-[#707EAE]">
                  Compare performance metrics across different time periods
                </p>
              </div>
              <div className="flex items-center gap-3">

                <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-2xl shadow-sm">
                  <span className="text-sm font-medium" style={{ color: colors.headingsBody }}>Show difference</span>
                  <button
                    onClick={() => setShowDifferenceChange(!showDifferenceChange)}
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#4318FF] focus:ring-offset-2 ${
                      showDifferenceChange ? 'bg-[#4318FF]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${
                        showDifferenceChange ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <button
                  onClick={handleExport}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#166473] hover:bg-[#166473] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7551FF] transition-colors duration-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Export Data
                </button>
              </div>
            </div>

            {/* Filter Set 1 */}
            <div className="bg-white rounded-2xl p-8 shadow border border-gray-100 mb-8">
              <div className="flex items-center mb-2">
                <span className="w-3 h-3 rounded-full bg-[#7551FF] mr-3"></span>
                <h3 className="text-lg font-bold text-[#2B3674] mr-3">Filter Set 1</h3>
                <span className="bg-[#F4F7FE] text-[#7551FF] px-3 py-1 rounded-full text-xs font-semibold">Comparison Period</span>
                <span className="ml-auto text-xs text-[#A3AED0] font-medium">Primary comparison data</span>
              </div>
              <Filters
                categoryDropdownRef={categoryDropdownRef1}
                showCategoryDropdown={showCategoryDropdown1}
                setShowCategoryDropdown={setShowCategoryDropdown1}
                selectedCategories={selectedCategories1}
                setSelectedCategories={setSelectedCategories1}
                categorySearchTerm={categorySearchTerm1}
                setCategorySearchTerm={setCategorySearchTerm1}
                filteredCategories={filteredCategories1}
                employeeDropdownRef={employeeDropdownRef1}
                showEmployeeDropdown={showEmployeeDropdown1}
                setShowEmployeeDropdown={setShowEmployeeDropdown1}
                selectedEmployees={selectedEmployees1}
                setSelectedEmployees={setSelectedEmployees1}
                employeeSearchTerm={employeeSearchTerm1}
                setEmployeeSearchTerm={setEmployeeSearchTerm1}
                filteredEmployees={filteredEmployees1}
                customerDropdownRef={customerDropdownRef1}
                showCustomerDropdown={showCustomerDropdown1}
                setShowCustomerDropdown={setShowCustomerDropdown1}
                selectedCustomers={selectedCustomers1}
                setSelectedCustomers={setSelectedCustomers1}
                customerSearchTerm={customerSearchTerm1}
                setCustomerSearchTerm={setCustomerSearchTerm1}
                filteredCustomers={filteredCustomers1}
                taskDropdownRef={taskDropdownRef1}
                showTaskDropdown={showTaskDropdown1}
                setShowTaskDropdown={setShowTaskDropdown1}
                selectedTasks={selectedTasks1}
                setSelectedTasks={setSelectedTasks1}
                taskSearchTerm={taskSearchTerm1}
                setTaskSearchTerm={setTaskSearchTerm1}
                filteredTasks={filteredTasks1}
                datePickerRef={datePickerRef1}
                showDatePicker={showDatePicker1}
                setShowDatePicker={setShowDatePicker1}
                dateRange={dateRange1}
                setDateRange={setDateRange1}
                fetchAnalytics={fetchAnalytics}
                fetchCharts={fetchChartsData}
                colorTheme="purple"
                showWarning={true}
              />
            </div>

            {/* Filter Set 2 */}
            <div className="bg-white rounded-2xl p-8 shadow border border-gray-100 mb-8">
              <div className="flex items-center mb-2">
                <span className="w-3 h-3 rounded-full bg-[#00B074] mr-3"></span>
                <h3 className="text-lg font-bold text-[#2B3674] mr-3">Filter Set 2</h3>
                <span className="bg-[#E6FAF0] text-[#00B074] px-3 py-1 rounded-full text-xs font-semibold">Base Period</span>
                <span className="ml-auto text-xs text-[#A3AED0] font-medium">Baseline comparison data</span>
              </div>
              <Filters
                categoryDropdownRef={categoryDropdownRef2}
                showCategoryDropdown={showCategoryDropdown2}
                setShowCategoryDropdown={setShowCategoryDropdown2}
                selectedCategories={selectedCategories2}
                setSelectedCategories={setSelectedCategories2}
                categorySearchTerm={categorySearchTerm2}
                setCategorySearchTerm={setCategorySearchTerm2}
                filteredCategories={filteredCategories2}
                employeeDropdownRef={employeeDropdownRef2}
                showEmployeeDropdown={showEmployeeDropdown2}
                setShowEmployeeDropdown={setShowEmployeeDropdown2}
                selectedEmployees={selectedEmployees2}
                setSelectedEmployees={setSelectedEmployees2}
                employeeSearchTerm={employeeSearchTerm2}
                setEmployeeSearchTerm={setEmployeeSearchTerm2}
                filteredEmployees={filteredEmployees2}
                customerDropdownRef={customerDropdownRef2}
                showCustomerDropdown={showCustomerDropdown2}
                setShowCustomerDropdown={setShowCustomerDropdown2}
                selectedCustomers={selectedCustomers2}
                setSelectedCustomers={setSelectedCustomers2}
                customerSearchTerm={customerSearchTerm2}
                setCustomerSearchTerm={setCustomerSearchTerm2}
                filteredCustomers={filteredCustomers2}
                taskDropdownRef={taskDropdownRef2}
                showTaskDropdown={showTaskDropdown2}
                setShowTaskDropdown={setShowTaskDropdown2}
                selectedTasks={selectedTasks2}
                setSelectedTasks={setSelectedTasks2}
                taskSearchTerm={taskSearchTerm2}
                setTaskSearchTerm={setTaskSearchTerm2}
                filteredTasks={filteredTasks2}
                datePickerRef={datePickerRef2}
                showDatePicker={showDatePicker2}
                setShowDatePicker={setShowDatePicker2}
                dateRange={dateRange2}
                setDateRange={setDateRange2}
                fetchAnalytics={fetchAnalytics}
                fetchCharts={fetchChartsData}
                colorTheme="green"
              />
            </div>

            {/* Action Bar */}
            <div className="flex gap-2 w-full md:w-auto mt-10 justify-end">
              <button
                className="bg-[#F4F7FE] px-4 py-1.5 rounded-full font-medium text-sm hover:bg-[#E9ECFF] transition"
                style={{ color: colors.headingsBody }}
                onClick={() => {
                  setSelectedCategories1([]);
                  setSelectedEmployees1([]);
                  setSelectedCustomers1([]);
                  setSelectedTasks1([]);
                  setDateRange1([{ startDate: null, endDate: null, key: 'selection1' }]);
                  setCategorySearchTerm1("");
                  setEmployeeSearchTerm1("");
                  setCustomerSearchTerm1("");
                  setTaskSearchTerm1("");
                  setSelectedCategories2([]);
                  setSelectedEmployees2([]);
                  setSelectedCustomers2([]);
                  setSelectedTasks2([]);
                  setDateRange2([{ startDate: null, endDate: null, key: 'selection2' }]);
                  setCategorySearchTerm2("");
                  setEmployeeSearchTerm2("");
                  setCustomerSearchTerm2("");
                  setTaskSearchTerm2("");
                  fetchAnalytics();
                  fetchChartsData();
                }}
              >
                Clear Filters
              </button>
              <button
                className="bg-[#166473] text-white px-4 py-1.5 rounded-full font-bold text-sm shadow hover:bg-[#166488] transition"
                onClick={() => {
                  fetchAnalytics();
                  fetchChartsData();
                }}
              >
                Apply Filters
              </button>
            </div>

            {/* Cards Section */}
            <div>
              <Cards 
                cards={cards} 
                analyticsLoading={analyticsLoading} 
                analytics={analytics?.filter2} 
                isComparison={true}
                comparisonMode={!showDifferenceChange}
                filter2Data={analytics?.filter1}
              />
            </div>
            <div>
              <Charts 
charts={chartConfigs}
                chartsLoading={chartsLoading}
                chartData={chartData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonDashboard; 